'use client';

import { c as _c } from "react/compiler-runtime";
import type { CardAISessionSummary } from '@shared/all/domains/electron';
import type { Post } from '@shared/all/domains/posts';
import toast from '@shared/utils/helpers/toast';
import { fetchPostBody } from '@shared/utils/hooks/usePostBody';
import { GitMerge, X } from 'lucide-react';
import { useState } from 'react';
import { useRunner } from '../../../hooks/useRunnerContext';
import { client } from '../../../utils/trpc';
import Button from '../../common/Button';
import ErrorStrip from '../../common/ErrorStrip';
import Modal, { ModalFooter, ModalHeader } from '../../HOC/Modal';
export type SelectedCard = {
  post: Post;
  factiiiId: number;
  // Whether the card had a completed (unpushed) AI session when selected.
  // Kinds never mix: changes-cards merge only with changes-cards.
  hasChanges: boolean;
};
type Props = {
  selected: SelectedCard[];
  onClear: () => void;
  // Called with the affected column ids after a successful merge so the
  // owner can invalidate caches and clear the selection.
  onMerged: (factiiiIds: number[]) => void;
};

// Floating action bar shown while cards are selected, plus the merge
// confirmation modal where the user picks which card survives.
const CardMergeBar = t0 => {
  const $ = _c(31);
  const {
    selected,
    onClear,
    onMerged
  } = t0;
  const {
    agent
  } = useRunner();
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  let t1;
  if ($[0] !== agent || $[1] !== loading || $[2] !== onMerged || $[3] !== selected) {
    t1 = async () => {
      if (selected.length < 2 || loading) {
        return;
      }
      if (!agent) {
        setError("Connect a runner to merge cards.");
        return;
      }
      setLoading(true);
      setError(null);
      let sessions;
      try {
        sessions = await agent.cardSessions();
      } catch {
        setLoading(false);
        setError("Could not reach the runner to check the cards\u2019 AI sessions. Try again.");
        return;
      }
      const selectedIds = selected.map(_temp);
      const running = selectedIds.filter(id => {
        const phase = sessions[id]?.phase;
        return phase != null && phase !== "complete";
      });
      if (running.length > 0) {
        setLoading(false);
        setError("Some selected cards have an active AI session. Wait for it to finish (or stop it) before merging.");
        return;
      }
      const sourcePostIds = selectedIds.filter(id_0 => sessions[id_0]?.phase === "complete");
      if (sourcePostIds.length > 0 && sourcePostIds.length < selectedIds.length) {
        setLoading(false);
        setError("Cards with unpushed AI changes can only be merged with other cards that have unpushed changes. Deselect the mismatched cards.");
        return;
      }
      let merged;
      ;
      try {
        const cards = await Promise.all(selected.map(_temp2));
        merged = await agent.cardMergeContent({
          cards
        });
      } catch (t2) {
        const e = t2;
        setLoading(false);
        setError(e instanceof Error ? e.message : "Failed to generate the merged card");
        return;
      }
      const [err, result] = await client.posts.merge.mutate({
        postIds: selectedIds,
        factiiiId: selected[0].factiiiId,
        title: merged.title,
        content: merged.content
      });
      if (err || !result) {
        setLoading(false);
        setError(err?.message || "Failed to merge cards");
        return;
      }
      if (agent && sourcePostIds.length > 0) {
        ;
        try {
          await agent.cardMergeSessions({
            primaryPostId: result.postId,
            sourcePostIds,
            taskTitle: result.title
          });
        } catch (t3) {
          const e_0 = t3;
          toast("Failed to merge card changes", e_0 instanceof Error ? e_0.message : "The card content was still merged", "error");
        }
      }
      setLoading(false);
      setModalOpen(false);
      onMerged(selected.map(_temp3));
    };
    $[0] = agent;
    $[1] = loading;
    $[2] = onMerged;
    $[3] = selected;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleMerge = t1;
  let t2;
  if ($[5] !== selected.length) {
    t2 = <span className="flex size-5 items-center justify-center rounded-md bg-primary font-mono text-[11px] font-medium tabular-nums text-white">{selected.length}</span>;
    $[5] = selected.length;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = selected.length !== 1 ? "s" : "";
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <span className="flex items-center gap-2 whitespace-nowrap text-xs text-textPrimary">{t2}card{t3} selected</span>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="h-4 w-px bg-ds-border-standard" />;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <GitMerge size={14} />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = selected.length < 2;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setModalOpen(true);
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7) {
    t9 = <Button variant="PRIMARY" text="Merge" icon={t6} disabled={t7} onClick={t8} classNames="text-xs" />;
    $[13] = t7;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <X size={14} />;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== onClear) {
    t11 = <button onClick={onClear} title="Clear selection" className="rounded-md p-1 text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary">{t10}</button>;
    $[16] = onClear;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t4 || $[20] !== t9) {
    t12 = <div className="animate-slideUp absolute bottom-4 left-1/2 z-[70] flex -translate-x-1/2 items-center gap-3 rounded-xl border border-ds-border-standard bg-background-primary px-3 py-2 shadow-lg">{t4}{t5}{t9}{t11}</div>;
    $[18] = t11;
    $[19] = t4;
    $[20] = t9;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== error || $[23] !== handleMerge || $[24] !== loading || $[25] !== modalOpen || $[26] !== selected) {
    t13 = modalOpen && <Modal closeHandler={() => setModalOpen(false)} maxWidth={520}><ModalHeader closeHandler={() => setModalOpen(false)}><h3 className="text-lg font-semibold text-textPrimary">Merge {selected.length} cards</h3></ModalHeader><div className="flex flex-col gap-3 p-4"><p className="text-xs leading-relaxed text-textSecondary">AI writes one new card combining everything below; the selected cards and their comments are deleted, and their attachments move to the merged card. If any card has unpushed AI changes, those are combined into a new review on the merged card for you to push.</p><div className="max-h-72 divide-y divide-ds-border-subtle overflow-y-auto rounded-lg border border-ds-border-standard">{selected.map(_temp4)}</div><p className="text-[10px] text-textSecondary">The merged card lands in the first selected card{"\u2019"}s column.</p>{error && <ErrorStrip error={error} mono={false} />}</div><ModalFooter><div className="flex items-center justify-end gap-2"><Button variant="SECONDARY" text="Cancel" onClick={() => setModalOpen(false)} /><Button variant="PRIMARY" text="Merge cards" icon={<GitMerge size={14} />} loading={loading} disabled={loading} onClick={() => void handleMerge()} /></div></ModalFooter></Modal>;
    $[22] = error;
    $[23] = handleMerge;
    $[24] = loading;
    $[25] = modalOpen;
    $[26] = selected;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t12 || $[29] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
};
export default CardMergeBar;
function _temp(s) {
  return s.post.id;
}
async function _temp2(t0) {
  const {
    post
  } = t0;
  return {
    title: post.title || "",
    content: await fetchPostBody(client, post)
  };
}
function _temp3(s_0) {
  return s_0.factiiiId;
}
function _temp4(t0, i) {
  const {
    post: post_0
  } = t0;
  return <div key={post_0.id} className="flex min-w-0 items-start gap-3 p-3"><span className="mt-px font-mono text-[11px] font-medium tabular-nums text-primary">{String(i + 1).padStart(2, "0")}</span><div className="min-w-0 flex-1"><span className="block truncate text-sm font-emphasis text-textPrimary">{post_0.title || "(untitled)"}</span>{post_0.content && <span className="line-clamp-2 text-xs text-textSecondary">{post_0.content}</span>}</div></div>;
}